import { Helmet } from "react-helmet-async";
import {
  Star,
  MessageCircleHeart,
  Filter,
  ShieldCheck,
  Sparkles,
  BarChart3,
  Reply,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import Stats from "../components/homepage/Stats";
import PageHero, {
  SectionHeader,
  FeatureGrid,
  NumberedStrip,
} from "../components/shared/PageHero";

const PILLARS = [
  {
    icon: Sparkles,
    title: "Smart In-App Prompts",
    text: "Trigger rating prompts at the precise moment a user experiences their first win — not at launch, not on crash.",
  },
  {
    icon: Filter,
    title: "Sentiment Pre-Filter",
    text: "Quietly route unhappy users to private feedback; route happy users to the public store review.",
  },
  {
    icon: Reply,
    title: "Reply Operations",
    text: "We reply to 100% of your 1–3★ reviews within 24h with human, on-brand, issue-resolving responses.",
  },
  {
    icon: MessageCircleHeart,
    title: "Review-Driven ASO",
    text: "Mine reviews for keywords, features and objections — then feed findings back into your listing copy.",
  },
  {
    icon: ShieldCheck,
    title: "Fake Review Defense",
    text: "Detect, report and clean competitor review attacks on both Apple App Store and Google Play.",
  },
  {
    icon: BarChart3,
    title: "Weekly Rating Dashboard",
    text: "Live rating trajectory, review volume, sentiment shifts and reply SLA — delivered every Monday.",
  },
];

const PROCESS = [
  { title: "Baseline", text: "Audit current rating, volume, review sentiment and prompt placement." },
  { title: "Prompt Rewire", text: "Implement contextual, event-based prompts with sentiment pre-filtering." },
  { title: "Reply Launch", text: "Our review ops team covers every new 1–3★ within 24 hours, daily." },
  { title: "Insight Loop", text: "Reviews become ASO intel — we feed learnings into metadata and screenshots." },
  { title: "Sustain", text: "Monthly sentiment review + quarterly prompt refresh. Rating stays high." },
];

export default function RatingsReviewsPage() {
  return (
    <>
      <Helmet>
        <title>
          App Ratings & Reviews Management — Boost Ratings 3★ to 4.5★ | Lumotis
        </title>
        <meta
          name="description"
          content="Lumotis's Ratings & Reviews service lifts app ratings from 3.x to 4.5+ in 60 days. Smart prompts, sentiment filtering, 24h review reply ops and review-driven ASO insights."
        />
        <meta
          name="keywords"
          content="app ratings management, app reviews management, increase app ratings, app review reply, rating optimization, iOS ratings, Google Play reviews, ASO reviews"
        />
        <link rel="canonical" href="https://lumotis.com/ratings-reviews" />
      </Helmet>
      <Navbar />
      <main data-testid="ratings-page">
        <PageHero
          testid="ratings-hero"
          eyebrow="Ratings & Reviews"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Ratings & Reviews" }]}
          title="Lift your app from 3★ to"
          titleAccent="4.5★ in 60 days."
          subtitle="Ratings are the #1 App Store conversion lever most founders ignore. We rebuild your prompt logic, run review reply ops and turn every review into ASO signal — on iOS and Google Play."
          primaryCta={{
            to: "/contact",
            label: "Audit my ratings",
            testid: "ratings-cta-primary",
          }}
          secondaryCta={{
            to: "/case-studies",
            label: "See rating wins",
            testid: "ratings-cta-secondary",
          }}
          rightSlot={
            <div className="bg-white border border-zinc-200 p-8 shadow-xl rounded-2xl">
              <p className="text-overline text-electric mb-5">
                Rating trajectory
              </p>
              <div className="space-y-4">
                {[
                  { market: "Global · iOS", b: 4.2, a: 4.8 },
                  { market: "USA · Google Play", b: 3.6, a: 4.6 },
                  { market: "UAE · iOS", b: 3.9, a: 4.7 },
                ].map((r) => (
                  <div
                    key={r.market}
                    className="flex items-center justify-between border-b border-zinc-200 pb-3 last:border-0"
                  >
                    <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                      {r.market}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-400 line-through text-sm">
                        {r.b}★
                      </span>
                      <span className="font-display text-2xl font-black text-electric">
                        {r.a}★
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-1 text-electric">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-electric" />
                ))}
                <span className="ml-2 text-xs uppercase tracking-widest text-zinc-500">
                  Avg. 60-day uplift
                </span>
              </div>
            </div>
          }
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Pillars"
              title="Six levers that move your app rating."
              description="Ratings are an operations problem, not a marketing problem. We run the operation."
            />
            <FeatureGrid items={PILLARS} testidPrefix="ratings-pillar" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mb-14">
              <p className="text-overline text-electric mb-3">02 — Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                From complaint hell to compounding 4.8★.
              </h2>
            </div>
            <NumberedStrip steps={PROCESS} testidPrefix="ratings-step" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white">
          <div className="container grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-overline text-electric mb-3">03 — Why it matters</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
                A 0.5★ rating lift = ~32% more installs at the same traffic.
              </h2>
              <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
                Ratings are the single biggest conversion lever on the App Store
                page. Every fraction of a star compounds into cheaper CPI, higher
                ASO rank, and more trust from first-time visitors. Done right,
                ratings become the flywheel — not the chore.
              </p>
            </div>
            <div className="lg:col-span-5 bg-zinc-50 border border-zinc-200 p-8">
              <Sparkles className="w-8 h-8 text-electric mb-4" />
              <p className="font-display text-xl font-bold text-charcoal leading-snug">
                “Lumotis took our health app from 4.2 to 4.8 in 60 days and
                cut negative reviews by 63%. It's the most underrated growth
                lever we've ever pulled.”
              </p>
              <p className="mt-4 text-sm text-zinc-500">
                — Amelia Carter, Head of Growth · Halo Health
              </p>
            </div>
          </div>
        </section>

        <Stats />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
